import { Data, Model } from '@singularsystems/neo-core';
import { AxiosPromise } from 'axios';
import { injectable } from 'inversify';
import { AppService, Types } from '../TransactionsTypes';
import TradeRequestCommand from '../Models/Trading/Commands/TradeRequestCommand';
import ParticipantBrokerDetail from 'st-common/Transactions/Models/ParticipantBrokerDetail';
import CancelTradeResult from 'st-common/Transactions/Models/Trading/CancelTradeResult';
import LinkedTradeRequestsCommand from '../Models/Trading/Commands/LinkedTradeRequestsCommand';
import { TradeLinkSaveResult, TradeRequestSaveResult } from '../Models/Trading/Lookups/TradeLinkSaveResult';
import TradeRequestLookup from '../Models/Trading/Lookups/TradeRequestLookup';
import CashTransactionLookup from 'st-common/Transactions/Models/CashTransactionLookup';
import UpdateParticipantBrokerDetailCommand from 'st-common/Transactions/Models/UpdateParticipantBrokerDetailCommand';
import TradeRequestAdminExtraDetailsLookup from 'st-common/Transactions/Models/Trading/TradeRequestAdminExtraDetailsLookup';
import { MinimumShareholdingSummary } from '../Models/Trading/Lookups/MinimumShareholdingSummary';

export interface ITradeApiClient {

    /**
     * Submits a trade request.
     * @param tradeRequest Trade request.
     * @returns Result.
     */
    submitTrade(tradeRequest: Model.PartialPlainObject<TradeRequestCommand>): AxiosPromise<Model.PlainObject<TradeRequestSaveResult>>;

    /**
     * Submits a linked trade request.
     * @param command Command.
     * @returns Trade ref no for each trade.
     */
    submitLinkedTrade(command: Model.PartialPlainObject<LinkedTradeRequestsCommand>): AxiosPromise<Model.PlainObject<TradeLinkSaveResult>>;

    /**
     * Cancels a trade request.
     * @param tradeRequestId Trade request id.
     * @returns Task.
     */
    cancelTrade(tradeRequestId: number): AxiosPromise<Model.PlainObject<CancelTradeResult>>;

    /**
     * Gets the current broker details for a participant.
     * @returns Broker details.
     */
    getParticipantBrokerDetails(participantBrokerDetailId?: number | null, tradeRequestId?: number | null): AxiosPromise<Model.PlainTrackedObject<ParticipantBrokerDetail> | null>;

    /**
     * Update the broker details of the current participant
     * @param participantBrokerDetailCommand Broker details.
     * @returns Task.
     */
    updateParticipantBrokerDetails(participantBrokerDetailCommand: Model.PartialPlainObject<UpdateParticipantBrokerDetailCommand>): AxiosPromise<Model.PlainTrackedObject<ParticipantBrokerDetail>>;

    /**
     * Gets a trade request for an authenticated participant.
     * Cash transactions child list will be populated.
     * @param tradeRequestId Trade request id.
     * @returns Trade request.
     */
    getTradeRequest(tradeRequestId: number): AxiosPromise<Model.PlainObject<TradeRequestLookup>>;

    /**
     * Gets a list of transactions for a trade request.
     * @param tradeRequestId Trade request id.
     * @returns List of cash transactions.
     */
    getTradeCashTransactions(tradeRequestId: number): AxiosPromise<Array<Model.PlainObject<CashTransactionLookup>>>;

    /**
     * Gets a list of transactions for a trade request.
     * @param tradeRequestId Trade request id.
     * @returns List of cash transactions.
     */
    getTradeRequestExtraDetails(tradeRequestId: number): AxiosPromise<Model.PlainObject<TradeRequestAdminExtraDetailsLookup>>;

    /**
     * Deactivate the specified participant broker details
     * @param participantBrokerDetailId The specific broker detail.
     * @returns Task.
     */
    deactivateParticipantBrokerDetails(participantBrokerDetailId: number): AxiosPromise;

    /**
     * Gets a download link for a document.
     * @param tradeRequestDocumentId The specific trade request document id
     * @returns Status.
     */
    getDocumentDownloadUrl(tradeRequestDocumentId: number): AxiosPromise<string>;

    /** 
     * Gets a summary of a participant's minimum shareholding requirement.
     * @returns A minimum shareholding summary.
     */
    getMSRParticipantSummary(): AxiosPromise<Model.PlainObject<MinimumShareholdingSummary>>;


    // Client only properties / methods
}

@injectable()
export default class TradeApiClient extends Data.ApiClientBase implements ITradeApiClient {

    constructor(config = AppService.get(Types.Shared.Config)) {
        super(`${config.TransactionsApi.ApiPath}/Trade`);
    }

    public submitTrade(tradeRequest: Model.PartialPlainObject<TradeRequestCommand>): AxiosPromise<Model.PlainObject<TradeRequestSaveResult>> {
        return this.axios.post(`${this.apiPath}/SubmitTrade`, tradeRequest);
    }

    public submitLinkedTrade(command: Model.PartialPlainObject<LinkedTradeRequestsCommand>): AxiosPromise<Model.PlainObject<TradeLinkSaveResult>> {
        return this.axios.post(`${this.apiPath}/SubmitLinkedTrade`, command);
    }

    public cancelTrade(tradeRequestId: number): AxiosPromise<Model.PlainObject<CancelTradeResult>> {
        return this.axios.post(`${this.apiPath}/CancelTrade/${tradeRequestId}`);
    }

    public getParticipantBrokerDetails(participantBrokerDetailId?: number | null, tradeRequestId?: number | null): AxiosPromise<Model.PlainTrackedObject<ParticipantBrokerDetail>> {
        let query = "";
        if (tradeRequestId) {
            query = `?tradeRequestId=${tradeRequestId}`;
        }

        return this.axios.get(`${this.apiPath}/ParticipantBrokerDetails/${participantBrokerDetailId ?? ""}${query}`);
    }

    public updateParticipantBrokerDetails(participantBrokerDetailCommand: Model.PartialPlainObject<UpdateParticipantBrokerDetailCommand>): AxiosPromise<Model.PlainTrackedObject<ParticipantBrokerDetail>> {
        return this.axios.post(`${this.apiPath}/updateParticipantBrokerDetails`, participantBrokerDetailCommand);
    }

    public getTradeRequest(tradeRequestId: number): AxiosPromise<Model.PlainObject<TradeRequestLookup>> {
        return this.axios.get(`${this.apiPath}/tradeRequest/${tradeRequestId}`);
    }

    public getTradeCashTransactions(tradeRequestId: number): AxiosPromise<Array<Model.PlainObject<CashTransactionLookup>>> {
        return this.axios.get(`${this.apiPath}/tradeRequestCashTransactions/${tradeRequestId}`);
    }

    public getTradeRequestExtraDetails(tradeRequestId: number): AxiosPromise<Model.PlainObject<TradeRequestAdminExtraDetailsLookup>> {
        return this.axios.get(`${this.apiPath}/tradeRequestExtraDetails/${tradeRequestId}`);
    }

    public deactivateParticipantBrokerDetails(participantBrokerDetailId: number): AxiosPromise {
        return this.axios.post(`${this.apiPath}/deactivateParticipantBrokerDetails?participantBrokerDetailId=${participantBrokerDetailId}`);
    }

    public getDocumentDownloadUrl(tradeRequestDocumentId: number): AxiosPromise<string> {
        return this.axios.post(`${this.apiPath}/documentDownloadUrl/${tradeRequestDocumentId}`);
    }

    public getMSRParticipantSummary(): AxiosPromise<Model.PlainObject<MinimumShareholdingSummary>> {
        return this.axios.get(`${this.apiPath}/minimum-shareholding`);
    }


    // Client only properties / methods
}